// Displays a linear countdown bar pinned to the top of the viewport.

import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/utilities/cn';
import { useMedia } from '../../hooks';
import { useAnyDialogOpen } from '@/components/ui/dialogPresenceStore';

interface LinearCountdownBarProps {
  endTime: number; // Timestamp in ms when the bar should reach zero
  height?: number; // Optional: height in px (default 8)
  timerTick?: boolean; // Optional: play timer tick SFX for last 5 seconds (default false)
}

const DEFAULT_HEIGHT = 8;

const LinearCountdownBar: React.FC<LinearCountdownBarProps> = ({
  endTime,
  height = DEFAULT_HEIGHT,
  timerTick = false,
}) => {
  const isDialogOpen = useAnyDialogOpen();
  const [fillFraction, setFillFraction] = useState(1);
  const initialDurationRef = useRef<number | null>(null);
  const isDialogOpenRef = useRef(isDialogOpen);
  const tickedSeconds = useRef<Set<number>>(new Set());
  const { playSoundEffect } = useMedia();

  useEffect(() => {
    isDialogOpenRef.current = isDialogOpen;
  }, [isDialogOpen]);

  useEffect(() => {
    if (typeof endTime !== 'number' || endTime <= 0) {
      return;
    }

    const now = Date.now();
    const initialTimeLeft = endTime - now;

    if (initialDurationRef.current === null) {
      initialDurationRef.current = initialTimeLeft;
    }

    const initialDuration = initialDurationRef.current;

    tickedSeconds.current.clear();

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, endTime - now);
      const fraction = Math.max(0, Math.min(1, remaining / initialDuration));

      if (!isDialogOpenRef.current) {
        setFillFraction(fraction);
      }

      if (timerTick && remaining > 0) {
        const secondsLeft = Math.floor(remaining / 1000);
        if (
          [5, 4, 3, 2, 1].includes(secondsLeft) &&
          !tickedSeconds.current.has(secondsLeft)
        ) {
          tickedSeconds.current.add(secondsLeft);
          playSoundEffect('timer_tick');
        }
      }

      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [endTime, timerTick, playSoundEffect]);

  if (typeof endTime !== 'number' || endTime <= Date.now()) {
    return null;
  }

  const indicatorColorClass =
    fillFraction <= 0.2
      ? 'bg-destructive'
      : fillFraction <= 0.4
        ? 'bg-warning'
        : 'bg-primary';

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[410]"
      style={{ height }}
    >
      <div
        role="progressbar"
        aria-valuenow={fillFraction}
        aria-valuemin={0}
        aria-valuemax={1}
        className={cn(
          'h-full transition-[width,background-color] duration-100 ease-linear',
          indicatorColorClass
        )}
        style={{ width: `${fillFraction * 100}%` }}
      />
    </div>
  );
};

export default LinearCountdownBar;
